/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  isFormFlowStateSuccess,
  whenFormInvalid,
} from '@gh/builder-shared';
import { AelEvents, ScheduleTaskType, ScheduleType } from '@gh/builder-shared-data';
import {
  code3Formatter,
  createDefaultGridModel,
  dictionaryValueFormatter,
  ROW_DIRTY_CLASS,
  ROW_GROUP_CLASS,
} from '@gh/common-internal-shared';
import {
  ActionFlow,
  DateTimeTypes,
  formStateSnapshot,
  getRouteInitData,
  getSingleNewSelectedEntity,
  TextTypes,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DateFormat, RouteDataService } from '@gh/core-data';
import {
  Confirmation,
  DialogService,
  noAnswer,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoCancelConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildEditableDateTimeColumn,
  buildEditableTextColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { toBoolean } from '@gh/core-util';
import { merge$, of$ } from '@gh/rx';
import { catchError$, filter$, map$, mapTo$, skip$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { ConstructionWorksheetBusinessService } from '../../resources/construction-worksheet-business.service';
import {
  CONSTRUCTION_WORKSHEET_DETAILS_INIT,
  ConstructionWorksheetDetails,
  constructionWorksheetMasterInfoFromSchedule,
  ConstructionWorksheetTask,
  constructionWorksheetTaskFieldState,
  getConstructionWorksheetTaskId,
  validateConstructionWorksheetTaskField,
} from '../../resources/construction-worksheet.model';
import { ScheduleFinderDialogConfig } from '../../resources/schedule-finder-business.service';
import { JobScheduleFinderListItem } from '../../resources/schedule-finder.model';
import { scheduleFinderDialogRoute } from '../schedule-finder-dialog/schedule-finder-dialog.route';
import { ConstructionWorksheetFormState } from './construction-worksheet-form.service';

@Component({
  selector: 'gh-construction-worksheet',
  template: require('./construction-worksheet.component.html'),
  providers: [ConstructionWorksheetFormState],
})
export class ConstructionWorksheetComponent extends AbstractFormComponent<ConstructionWorksheetDetails> implements OnInit {
  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private waitings: WaitingService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              public form: ConstructionWorksheetFormState,
              private business: ConstructionWorksheetBusinessService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigations: BuilderNavigationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions) {
    super();
  }

  undo(): void {
    this.form.undo();
  }

  redo(): void {
    this.form.redo();
  }

  get completeFlow(): ActionFlow<FormFlowState<ConstructionWorksheetDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(({ value }) => this.business.update(value)),
      finish: ({ value }) => {
        this.form.build(value);
      },
    });
  }

  ngOnInit(): void {
    const { details: initDetails } = getRouteInitData(this.route);

    this.form.build(initDetails);

    this.gridDefinition = this.buildGridDefinition();

    this.route
      .queryParams.pipe(
      skip$(1),
      switchMap$(({ scheduleId }) => scheduleId
        ? this.business.get(scheduleId).pipe(
          (this.waitings.operator()),
          catchError$((error) => {
            this.clearScheduleQueryParam();
            this.notifications.processAll(error);
            return [];
          }))
        : [CONSTRUCTION_WORKSHEET_DETAILS_INIT]))
      .subscribe((details) => this.form.build(details));
  }

  onScheduleSearch(): void {
    this.confirmSave()
      .subscribe(() => {
        this.dialogs.openWithInput(scheduleFinderDialogRoute, <ScheduleFinderDialogConfig>{
          type: ScheduleType.Construction,
          multiselection: false,
        }).afterClosed().pipe(
          filter$(Boolean),
          map$(getSingleNewSelectedEntity),
          // if the same schedule was selected => we have to reset selected schedule first
          // we cannot just open the same schedule, because we do this via route query params
          // (if query params no changed => form is not refreshed)
          switchMap$((schedule: JobScheduleFinderListItem) => this.clearScheduleQueryParam().pipe(mapTo$(schedule))))
          .subscribe((schedule: JobScheduleFinderListItem) => {
            this.form.setMasterInfo(formStateSnapshot(constructionWorksheetMasterInfoFromSchedule(schedule)));
            this.setScheduleQueryParam(schedule.scheduleId);
          });
      });
  }

  onScheduleClear(): void {
    this.confirmSave()
      .subscribe(() => this.clearScheduleQueryParam());
  }

  onShowCompletedTasksChange(showCompletedTasks: boolean): void {
    this.form.setShowCompletedTasks(showCompletedTasks);
  }

  onFocusedCellChange(): void {
    this.form.resetFocusedCell();
  }

  goToAel(): void {
    this.navigations.goToAel({
      label: 'lbl_construction_worksheet',
      entityType: 'JOB',
      eventTypeIds: [AelEvents.Job.ConstructionSchedule.Edited],
    });
  }

  private buildGridDefinition(): GridDefinition {
    const { gridLayout, taskTypeDict } = getRouteInitData(this.route);
    const model = createDefaultGridModel({
      editable: this.form.editable$,
      getId: getConstructionWorksheetTaskId,
      validate: validateConstructionWorksheetTaskField,
      getCellDef: constructionWorksheetTaskFieldState,
      command: (entity, name, value) => this.form.setTaskField(getConstructionWorksheetTaskId(entity), name, value),
    });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      layout: gridLayout,
      model,
      columns: {
        taskNumber: {
          valueFormatter: code3Formatter,
        },
        type: {
          valueFormatter: dictionaryValueFormatter(taskTypeDict),
        },
        startDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        completionDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        confirmedWith: buildEditableTextColumn(TextTypes.Text255, model.getCellAccessor('confirmedWith')),
        confirmedOnDate: buildEditableDateTimeColumn(
          DateTimeTypes.ShortDate,
          model.getCellAccessor('confirmedOnDate'),
          {
            format: DateFormat.Date,
          }),
        confirmedForDate: buildEditableDateTimeColumn(
          DateTimeTypes.ShortDate,
          model.getCellAccessor('confirmedForDate'),
          {
            format: DateFormat.Date,
          }),
      },
      options: {
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_GROUP_CLASS]: ({ data: { type } }: { data: ConstructionWorksheetTask }) =>
            type === ScheduleTaskType.CriticalPath || type === ScheduleTaskType.Summary,
          [ROW_DIRTY_CLASS]: ({ data: { isDirty } }: { data: ConstructionWorksheetTask }) => toBoolean(isDirty),
        },
      },
    });
  }

  private setScheduleQueryParam(id: ID): Observable<boolean> {
    return this.routeData.mergeQueryParams({ scheduleId: id });
  }

  private clearScheduleQueryParam(): Observable<boolean> {
    return this.routeData.mergeQueryParams({ scheduleId: void 0 });
  }

  private handleErrors(): void {
    this.form.focusFirstInvalidTask();
  }

  private confirmSave(): Observable<Confirmation> {
    if (this.form.dirty) {
      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

      const yes$ = confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => {
          const flow = this.completeFlow;
          return flow.run({
            value: this.form.submitValue,
            form: this.form,
            trigger: 'process',
          }).toFinalState().pipe(
            filter$(isFormFlowStateSuccess));
        }),
        mapTo$(Confirmation.Yes));
      const no$ = confirmation$.pipe(filter$(noAnswer));

      return merge$(yes$, no$);
    } else {
      return of$(Confirmation.Yes);
    }
  }
}
